import React from 'react';
import './mod6.css';
import responseibleai from './mod6images/responsibleai.png';
import stakeholders from './mod6images/stakeholders.png';
const Mod6Sec3 = () => {
    return (
        <div className="mod6sec1-container">
                <h1>Lesson 3: Future Trends and Collective Responsibility</h1>
                <h3>Overview</h3> 
                <p>
                    As artificial intelligence (AI) continues to permeate various aspects of society, the importance of accountability and collective responsibility becomes increasingly pronounced. This lesson explores emerging trends in AI accountability and the collaborative approaches necessary for fostering responsible AI governance. By understanding the dynamics shaping the future of accountability in AI, we can better navigate the challenges and opportunities presented by these technologies.
                </p>

                <h3>Learning Objectives</h3>
                <ul>
                    <li>Identify Emerging Trends: Understand the key innovations and trends shaping the future of accountability in AI.</li>
                    <li>Analyze Public Expectations: Examine the role of public expectations and societal values in driving AI accountability.</li>
                    <li>Recognize Collaborative Approaches: Explore the importance of stakeholder engagement in AI governance and the benefits of collaborative initiatives.</li>
                    <li>Evaluate Successful Partnerships: Analyze successful examples of partnerships that promote accountability in AI systems.</li>
                    <li>Foster Collective Responsibility: Understand the significance of collective responsibility in creating ethical and accountable AI technologies.</li>
                </ul>

                <h3>I. Emerging Trends in AI Accountability</h3>
                <h4>Innovations and Trends Shaping the Future of Accountability in AI</h4>
                <ul>
                    <li>
                        <strong>Transparency-Enhancing Technologies:</strong> With rising concerns over AI decision-making processes, transparency-enhancing technologies, such as explainable AI (XAI) and interpretable machine learning models, are gaining traction.
                    </li>
                    <li>
                        <strong>AI Ethics Guidelines and Frameworks:</strong> Numerous organizations and governments are developing ethical guidelines and frameworks to govern AI development.
                    </li>
                    <li>
                        <strong>Data Stewardship and Governance:</strong> As data privacy regulations like GDPR and CCPA continue to evolve, organizations are increasingly focusing on data stewardship and governance.
                    </li>
                    <li>
                        <strong>Automated Auditing Tools:</strong> The development of automated auditing tools allows organizations to assess their AI systems for compliance with ethical guidelines and regulations.
                    </li>
                    <li>
                        <strong>Public Engagement and Crowdsourcing:</strong> Engaging the public in AI development and accountability initiatives is an emerging trend.
                    </li>
                </ul>

                <h4>The Role of Public Expectations and Societal Values in AI Development</h4>
                <div className="text-image-container">
                     { <img src={responseibleai} alt="Responsible AI" className="res-ai-img" /> }
                </div>               
                <ul>
                    <li><strong>Demand for Ethical AI:</strong> As public awareness of AI's impact grows, individuals increasingly demand ethical AI practices.</li>
                    <li><strong>Social Impact Considerations:</strong> Societal values influence AI development by emphasizing the importance of social impact.</li>
                    <li><strong>Diversity and Inclusion:</strong> Public expectations increasingly emphasize the importance of diversity and inclusion in AI development.</li>
                    <li><strong>Regulatory Pressure:</strong> Governments and regulatory bodies are increasingly holding organizations accountable for their AI practices.</li>
                    <li><strong>Ethical Consumerism:</strong> Consumers are becoming more discerning about the products and services they use, favoring companies that prioritize ethical practices.</li>
                </ul>

                <h3>II. Collaborative Approaches to Responsibility</h3>
                <h4>Importance of Stakeholder Engagement in AI Governance</h4>
                <div className="text-image-container">
                     { <img src={stakeholders} alt="Importance of Stakeholders" className="stake-ai-img" /> }
                </div>                      
                <ul>
                    <li>
                        <strong>Multi-Stakeholder Collaborations:</strong> Engaging diverse stakeholders is essential for creating a comprehensive framework for AI accountability.
                    </li>
                    <li>
                        <strong>Public-Private Partnerships:</strong> Collaborations between public and private sectors can drive innovation while ensuring accountability.
                    </li>
                    <li>
                        <strong>User-Centric Approaches:</strong> Involving users in the design and development of AI systems can lead to better outcomes.
                    </li>
                    <li>
                        <strong>Community Engagement:</strong> Engaging local communities in discussions about AI accountability can lead to more responsible practices.
                    </li>
                </ul>

                <h3>Real-Life Implications of Accountability and Responsibility in AI</h3>
                <ul>
                    <li>
                        <strong>Uber’s Self-Driving Car Fatality (2018):</strong> In March 2018, a self-driving car operated by Uber struck and killed a pedestrian in Tempe, Arizona.
                    </li>
                    <li>
                        <strong>COMPAS Algorithm in Criminal Justice:</strong> The Correctional Offender Management Profiling for Alternative Sanctions (COMPAS) algorithm is used in the United States to assess the likelihood of a defendant reoffending.
                    </li>
                    <li>
                        <strong>Facial Recognition Technology and Racial Bias:</strong> Numerous studies have shown that facial recognition technology exhibits racial bias.
                    </li>
                </ul>

                <h3>III. Fostering Collective Responsibility in AI</h3>
                <h4>Understanding the Significance of Collective Responsibility in Ethical AI</h4>
                <ul>
                    <li>
                        <strong>Shared Accountability:</strong> Collective responsibility emphasizes that accountability for AI systems does not rest solely with one entity.
                    </li>
                    <li>
                        <strong>Building a Culture of Ethics:</strong> Organizations must cultivate a culture of ethics where all employees are encouraged to prioritize ethical considerations in their work with AI.
                    </li>
                    <li>
                        <strong>Engaging in Dialogue:</strong> Ongoing dialogue among stakeholders is essential for understanding diverse perspectives on accountability and ethics in AI.
                    </li>
                    <li>
                        <strong>Advocating for Policy Changes:</strong> Organizations can take collective action to advocate for policy changes that promote accountability and ethical AI practices.
                    </li>
                </ul>
        </div>
    );
};

export default Mod6Sec3;
